import { v4 as uuidv4 } from "uuid";

// Type definitions for bug report data
export interface BugReportContext {
  error: Error;
  userId?: string;
  threadId?: string;
  agentId?: string;
  url?: string;
  timestamp?: Date;
  userAgent?: string;
  sessionData?: any;
  messageHistory?: any[];
  modelProvider?: string;
  recentMessages?: any[];
}

export interface BugReportData {
  reportId: string;
  timestamp: string;
  error: {
    name: string;
    message: string;
    stack?: string;
  };
  userContext: {
    userId?: string;
    userRole?: string;
    sessionId?: string;
    email?: string;
  };
  chatContext: {
    threadId?: string;
    agentId?: string;
    messageHistory?: any[];
  };
  browserInfo: {
    userAgent: string;
    url: string;
    viewport: {
      width: number;
      height: number;
    };
    language: string;
    platform: string;
  };
  systemInfo: {
    timestamp: string;
    timezone: string;
    screen: {
      width: number;
      height: number;
      colorDepth: number;
    };
  };
  userInput: string;
  screenshot?: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export class BugReportService {
  /**
   * Collect comprehensive error data from the provided context
   */
  static collectErrorData(
    context: BugReportContext,
  ): Omit<BugReportData, "userInput" | "screenshot"> {
    const reportId = uuidv4();
    const timestamp = new Date().toISOString();

    return {
      reportId,
      timestamp,
      error: {
        name: context.error.name || "UnknownError",
        message: context.error.message || "No error message provided",
        stack: this.sanitizeStackTrace(context.error.stack),
      },
      userContext: {
        userId: context.userId,
        userRole: this.extractUserRole(context.sessionData),
        sessionId: this.extractSessionId(context.sessionData),
        email: this.extractUserEmail(context.sessionData),
      },
      chatContext: {
        threadId: context.threadId,
        agentId: context.agentId,
        messageHistory: this.sanitizeMessageHistory(context.messageHistory),
      },
      browserInfo: {
        userAgent: context.userAgent || navigator.userAgent,
        url: context.url || window.location.href,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        language: navigator.language,
        platform: navigator.platform,
      },
      systemInfo: {
        timestamp,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screen: {
          width: screen.width,
          height: screen.height,
          colorDepth: screen.colorDepth,
        },
      },
    };
  }

  /**
   * Validate bug report data before submission
   */
  static validateBugReportData(data: BugReportData): ValidationResult {
    const errors: string[] = [];

    // Required fields
    if (!data.reportId) errors.push("Report ID is required");
    if (!data.timestamp) errors.push("Timestamp is required");
    if (!data.error?.name) errors.push("Error name is required");
    if (!data.error?.message) errors.push("Error message is required");
    if (!data.userInput?.trim()) errors.push("User description is required");

    // Data format validation
    try {
      new Date(data.timestamp);
    } catch {
      errors.push("Invalid timestamp format");
    }

    if (data.userInput && data.userInput.length > 5000) {
      errors.push("User description is too long (max 5000 characters)");
    }

    // Screenshot validation (if provided)
    if (data.screenshot) {
      if (!data.screenshot.startsWith("data:image/")) {
        errors.push("Invalid screenshot format");
      }
      // Check size (rough estimate: base64 is ~4/3 the size of binary)
      const estimatedSize = (data.screenshot.length * 3) / 4;
      if (estimatedSize > 10 * 1024 * 1024) {
        // 10MB limit
        errors.push("Screenshot is too large (max 10MB)");
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Sanitize stack trace to remove sensitive information
   */
  private static sanitizeStackTrace(stack?: string): string | undefined {
    if (!stack) return undefined;

    return (
      stack
        // Remove file paths that might contain usernames
        .replace(/\/Users\/[^\/]+/g, "/Users/[user]")
        .replace(/C:\\Users\\[^\\]+/g, "C:\\Users\\[user]")
        // Remove potential API keys or tokens in URLs
        .replace(
          /[?&](api[_-]?key|token|secret|password)=[^&\s]+/gi,
          "$1=[REDACTED]",
        )
        // Remove potential email addresses
        .replace(
          /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
          "[EMAIL_REDACTED]",
        )
        // Limit stack trace length
        .split("\n")
        .slice(0, 20)
        .join("\n")
    );
  }

  /**
   * Extract user role from session data safely
   */
  private static extractUserRole(sessionData: any): string | undefined {
    try {
      return sessionData?.user?.role || sessionData?.role;
    } catch {
      return undefined;
    }
  }

  /**
   * Extract session ID from session data safely
   */
  private static extractSessionId(sessionData: any): string | undefined {
    try {
      return sessionData?.sessionId || sessionData?.id;
    } catch {
      return undefined;
    }
  }

  /**
   * Extract user email from session data safely
   */
  private static extractUserEmail(sessionData: any): string | undefined {
    try {
      return sessionData?.user?.email || sessionData?.email;
    } catch {
      return undefined;
    }
  }

  /**
   * Sanitize message history to remove sensitive information
   */
  private static sanitizeMessageHistory(messages?: any[]): any[] | undefined {
    if (!messages || !Array.isArray(messages)) return undefined;

    return messages
      .slice(-10) // Only include last 10 messages
      .map((message) => {
        if (typeof message !== "object") return message;

        const sanitized = { ...message };

        // Remove potential sensitive content
        if (sanitized.content) {
          sanitized.content = this.sanitizeText(sanitized.content);
        }
        if (sanitized.text) {
          sanitized.text = this.sanitizeText(sanitized.text);
        }

        // Keep only essential fields
        return {
          id: sanitized.id,
          role: sanitized.role,
          content: sanitized.content,
          text: sanitized.text,
          timestamp: sanitized.timestamp,
          type: sanitized.type,
        };
      });
  }

  /**
   * Sanitize text content to remove sensitive information
   */
  private static sanitizeText(text: string): string {
    if (!text || typeof text !== "string") return text;

    return (
      text
        // Remove email addresses
        .replace(
          /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
          "[EMAIL_REDACTED]",
        )
        // Remove potential API keys, tokens, passwords
        .replace(
          /(?:api[_-]?key|token|secret|password|pwd)\s*[:=]\s*['"]?[a-zA-Z0-9+/=_-]+['"]?/gi,
          "$1: [REDACTED]",
        )
        // Remove potential phone numbers
        .replace(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, "[PHONE_REDACTED]")
        // Remove credit card numbers
        .replace(
          /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g,
          "[CARD_REDACTED]",
        )
        // Truncate if too long
        .substring(0, 1000)
    );
  }

  /**
   * Generate a unique report ID
   */
  static generateReportId(): string {
    return uuidv4();
  }

  /**
   * Check if bug reporting is enabled
   */
  static isBugReportingEnabled(): boolean {
    return process.env.BUG_REPORT_ENABLED === "true";
  }

  /**
   * Get bug report email address
   */
  static getBugReportEmail(): string | undefined {
    return process.env.BUG_REPORT_EMAIL;
  }
}
